"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  DUTCH_PASS_MARK,
  dutchPoints,
  dutchRow,
  PAIR_RULES,
  type PairRule,
  summariseDutch,
} from "@/lib/tool-kit/intl-grades/dutch-grades";

/** English strings. A translated content file overrides these through `ui`. */
export const DUTCH_GRADE_CONVERTER_STRINGS = {
  ruleLabel: "Where Nuffic gives two letters (A/A−, B−/C)",
  rules: {
    upper: "Use the higher letter",
    middle: "Use the midpoint",
    lower: "Use the lower letter",
  },
  ruleHint:
    "Nuffic's table gives a pair for a Dutch 7.5 and a 6. The same choice sets the GPA for those grades.",
  coursesHeading: "Your Dutch grades (1–10)",
  gradeLabel: "Grade for course {n}",
  creditsLabel: "Credits (ECTS) for course {n}",
  addCourse: "Add a course",
  removeCourse: "Remove",
  removeCourseAria: "Remove course {n}",
  results: "Result",
  average: "Weighted average (1–10)",
  gpa: "GPA (4.0 scale)",
  usLetter: "US letter for the average",
  ukClass: "UK class for the average",
  ukClasses: {
    first: "First",
    "upper-second": "Upper second (2:1)",
    "lower-second": "Lower second (2:2)",
    third: "Third",
    fail: "Fail",
  },
  belowPass: "Courses below {pass}: {count}",
  tableHeading: "Course by course",
  colCourse: "Course",
  colGrade: "Grade",
  colUs: "US",
  colUk: "UK",
  colClass: "UK class",
  colPoints: "Grade points",
  colCredits: "Credits",
  courseName: "Course {n}",
  skipped: "Skipped (not a grade from 1 to 10, or no credits): {rows}",
  empty: "Enter at least one grade from 1 to 10 with credits above zero.",
  indicative:
    "The UK class is Nuffic's indicative match for a Dutch average. A UK university classifies degrees by its own algorithm, so treat it as a guide.",
  rarity:
    "Dutch grades of 9 and 10 are rarely given and 6 and 7 are the most common, so a Dutch 8 is a strong result — which is why a straight 10 = A, 9 = B reading is wrong.",
  sourceNote:
    "Source: Nuffic, Grading systems in the Netherlands, the United States and the United Kingdom (2009). The table reflects Nuffic's opinion; a grade between two rows is read in the row below it.",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "Dutch average {average} = GPA {gpa} (US {letter}, UK {ukClass})",
} as const;

type Strings = typeof DUTCH_GRADE_CONVERTER_STRINGS;
type Format = ReturnType<typeof useToolNumber>;

interface Row {
  id: number;
  grade: string;
  credits: string;
}

const INITIAL_ROWS: Row[] = [
  { id: 1, grade: "8", credits: "6" },
  { id: 2, grade: "7.5", credits: "6" },
  { id: 3, grade: "6.5", credits: "6" },
  { id: 4, grade: "6", credits: "3" },
  { id: 5, grade: "5", credits: "3" },
];

const TWO_DECIMALS = { minimumFractionDigits: 2, maximumFractionDigits: 2 };

function CourseRow({
  row,
  index,
  strings,
  fmt,
  onChange,
  onRemove,
}: {
  row: Row;
  index: number;
  strings: Strings;
  fmt: Format;
  onChange: (next: Row) => void;
  onRemove: () => void;
}) {
  const n = fmt(index + 1);
  return (
    <div className="grid items-end gap-3 sm:grid-cols-[1fr_1fr_auto]">
      <TextField
        id={`dgc-grade-${row.id}`}
        label={t(strings.gradeLabel, { n })}
        numeric
        onChange={(grade) => onChange({ ...row, grade })}
        value={row.grade}
      />
      <TextField
        id={`dgc-credits-${row.id}`}
        label={t(strings.creditsLabel, { n })}
        numeric
        onChange={(credits) => onChange({ ...row, credits })}
        value={row.credits}
      />
      <button
        aria-label={t(strings.removeCourseAria, { n })}
        className={BUTTON_SECONDARY}
        onClick={onRemove}
        type="button"
      >
        {strings.removeCourse}
      </button>
    </div>
  );
}

export default function DutchGradeConverterWidget() {
  const strings = useToolUi(DUTCH_GRADE_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [rule, setRule] = useState<PairRule>("middle");
  const [rows, setRows] = useState<Row[]>(INITIAL_ROWS);
  const [nextId, setNextId] = useState(6);

  const parsed = rows.map((row) => ({
    grade: parseNumberInput(row.grade),
    credits: parseNumberInput(row.credits),
  }));
  const courses = parsed.flatMap(({ grade, credits }) =>
    grade === null || credits === null ? [] : [{ grade, credits }]
  );
  const summary = summariseDutch(courses, rule);
  const skipped = parsed.flatMap(({ grade, credits }, index) =>
    grade !== null && credits !== null && credits > 0 && dutchRow(grade)
      ? []
      : [fmt(index + 1)]
  );

  const tableRows = parsed.flatMap(({ grade, credits }, index) => {
    const row = grade === null ? null : dutchRow(grade);
    if (grade === null || credits === null || row === null || credits <= 0) {
      return [];
    }
    return [
      [
        t(strings.courseName, { n: fmt(index + 1) }),
        fmt(grade, { maximumFractionDigits: 2 }),
        row.us.join("/"),
        row.uk.join("/"),
        strings.ukClasses[row.ukClass],
        fmt(dutchPoints(row, rule), TWO_DECIMALS),
        fmt(credits, { maximumFractionDigits: 2 }),
      ],
    ];
  });

  return (
    <div className="space-y-6">
      <div className={`${CARD} space-y-5`}>
        <SelectField
          hint={strings.ruleHint}
          id="dgc-rule"
          label={strings.ruleLabel}
          onChange={setRule}
          options={PAIR_RULES.map((value) => ({
            value,
            label: strings.rules[value],
          }))}
          value={rule}
        />
        <h3 className={WIDGET_H3}>{strings.coursesHeading}</h3>
        <div className="space-y-3">
          {rows.map((row, index) => (
            <CourseRow
              fmt={fmt}
              index={index}
              key={row.id}
              onChange={(next) =>
                setRows((current) =>
                  current.map((item) => (item.id === row.id ? next : item))
                )
              }
              onRemove={() =>
                setRows((current) =>
                  current.filter((item) => item.id !== row.id)
                )
              }
              row={row}
              strings={strings}
            />
          ))}
        </div>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setRows((current) => [
              ...current,
              { id: nextId, grade: "", credits: "6" },
            ]);
            setNextId((value) => value + 1);
          }}
          type="button"
        >
          {strings.addCourse}
        </button>
      </div>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {summary === null ? (
          <Notice>{strings.empty}</Notice>
        ) : (
          <>
            <StatGrid>
              <StatCard
                label={strings.average}
                value={fmt(summary.average, TWO_DECIMALS)}
              />
              <StatCard
                emphasis
                label={strings.gpa}
                value={fmt(summary.gpa, TWO_DECIMALS)}
              />
              <StatCard
                label={strings.usLetter}
                value={summary.row.us.join("/")}
              />
              <StatCard
                label={strings.ukClass}
                value={strings.ukClasses[summary.row.ukClass]}
              />
            </StatGrid>
            {summary.belowPass > 0 ? (
              <p className="text-foreground text-sm">
                {t(strings.belowPass, {
                  pass: fmt(DUTCH_PASS_MARK, { minimumFractionDigits: 1 }),
                  count: fmt(summary.belowPass),
                })}
              </p>
            ) : null}
            {skipped.length > 0 ? (
              <p className="text-muted-foreground text-sm">
                {t(strings.skipped, { rows: skipped.join(", ") })}
              </p>
            ) : null}
            <DataTable
              caption={strings.tableHeading}
              head={[
                strings.colCourse,
                strings.colGrade,
                strings.colUs,
                strings.colUk,
                strings.colClass,
                strings.colPoints,
                strings.colCredits,
              ]}
              rows={tableRows}
            />
            <p className="text-muted-foreground text-sm">
              {strings.indicative}
            </p>
            <p className="text-muted-foreground text-sm">{strings.rarity}</p>
            <p className="text-muted-foreground text-sm">
              {strings.sourceNote}
            </p>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyResult}
              small
              text={t(strings.summary, {
                average: fmt(summary.average, TWO_DECIMALS),
                gpa: fmt(summary.gpa, TWO_DECIMALS),
                letter: summary.row.us.join("/"),
                ukClass: strings.ukClasses[summary.row.ukClass],
              })}
            />
          </>
        )}
      </section>
    </div>
  );
}
